import { connect } from "react-redux";
import { RootState } from "src/stores";
import { fetchAllUsers } from "src/stores/user";
import { useState, Fragment, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dialog as HeadlessDialog, Transition } from "@headlessui/react";
import Select from "react-select";
import {
  fetchReminderCalendarService,
  completeReminderService,
  snoozeReminderService,
  fetchReminderByIdService,
  deleteReminderService,
} from "src/services/reminder.services";

import Card from "src/components/Card";
import Button from "src/base-components/Button";
import Lucide from "src/base-components/Lucide";
import {
  FormInput,
  FormLabel,
  FormTextarea,
  FormSelect,
} from "src/base-components/Form";
import ReactSelect from "src/base-components/ReactSelect";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TailwindDatePicker from "src/base-components/DatePicker";
import { DateValueType } from "react-tailwindcss-datepicker";
import moment from "moment";
import ReminderCalendar from "src/components/ReminderCalendar";
import CreateReminderPanel from "src/themes/Razor/CreateReminderPanel";
import ReminderExport from "src/pages/Reminders/ReminderExport";
import Tippy from "src/base-components/Tippy";
import type { ReminderEvent, ReminderType } from "src/types/reminder";
import { checkPermission, isSuperAdmin } from "src/helpers";
import { reminderAPIUrls } from "src/common/apiActions";

// List View Component
interface ReminderListViewProps {
  events: ReminderEvent[];
  onEventClick: (event: ReminderEvent) => void;
  loading: boolean;
  error: string | null;
  currentSearch: string;
  currentFilters: {
    reminderType?: string;
    status?: string;
    userId?: string | number | (string | number)[] | undefined;
  };
  onSearchChange: (searchTerm: string) => void;
  onFilterChange: (filters: {
    reminderType?: string;
    status?: string;
    userId?: string | number | (string | number)[] | undefined;
  }) => void;
  onRetry: () => void;
  onEditReminder?: (reminderId: string) => void;
  onDeleteReminder?: (reminderId: string) => void;
  onDateRangeChange?: (dateRange: DateValueType) => void;
  // Sorting (prop-driven from parent)
  sortBy: "created_at" | "remind_on" | undefined;
  sortOrder: "asc" | "desc";
  onSortByChange: (value: "created_at" | "remind_on" | undefined) => void;
  onSortOrderToggle: () => void;
  agentOptions?: { label: string; value: number }[];
  selectedAgentId?: string | number | undefined;
  filterDate: DateValueType | null;
  setFilterDate: (dateRange: DateValueType) => void;
  setClearFiltersListTrigger: (val: boolean) => void;
  search: string;
  setSearch: (value: string) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;

  selectedAgentList: (number | string)[];
  setSelectedAgentList: (agent: (number | string)[]) => void;
}

const ReminderListView = ({
  events,
  onEventClick,
  loading,
  error,
  currentSearch,
  currentFilters,
  selectedAgentList,
  setSelectedAgentList,
  filterDate,
  setFilterDate,
  onSearchChange,
  onFilterChange,
  onRetry,
  onEditReminder,
  onDeleteReminder,
  onDateRangeChange,
  sortBy,
  sortOrder,
  agentOptions,
  onSortByChange,
  onSortOrderToggle,
  setClearFiltersListTrigger,
  selectedAgentId,
  currentPage,
  setCurrentPage,
}: ReminderListViewProps) => {
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const sortMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      try {
        if (!sortMenuOpen) return;
        const el = sortMenuRef.current;
        if (el && !el.contains(e.target as Node)) {
          setSortMenuOpen(false);
        }
      } catch {}
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [sortMenuOpen]);
  console.log("filterDate---->filterDate", filterDate);
  const getStatusBadge = (status: string) => {
    const statusMap = {
      completed: {
        bg: "bg-green-100",
        text: "text-green-700",
        label: "Completed",
      },
      overdue: { bg: "bg-red-100", text: "text-red-700", label: "Overdue" },
      snoozed: {
        bg: "bg-orange-100",
        text: "text-orange-700",
        label: "Snoozed",
      },
      pending: { bg: "bg-blue-100", text: "text-blue-700", label: "Active" },
    };
    const config =
      statusMap[status as keyof typeof statusMap] || statusMap.pending;
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        {config.label}
      </span>
    );
  };

  // Calculate statistics (matching calendar view logic)
  const today = moment().format("YYYY-MM-DD");
  const totalReminders = events.filter(
    (event) => event.status !== "completed"
  ).length;
  const dueTodayCount = events.filter(
    (event) =>
      moment(event.start).format("YYYY-MM-DD") === today &&
      event.status !== "completed"
  ).length;
  const overdueCount = events.filter(
    (event) =>
      moment(event.start).isBefore(today, "day") && event.status !== "completed"
  ).length;
  const completedCount = events.filter(
    (event) => event.status === "completed"
  ).length;

  // Pagination indices
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEvents = events.slice(startIndex, endIndex);
  const totalPages = Math.ceil(events.length / itemsPerPage);

  console.log(paginatedEvents, "paginatedEvents");

  const onAgentChange = (ids: number[] | string[]) => {
    // If parent handles filter change
    if (onFilterChange) {
      // triggerParentLoading();
      try {
        const userIdStr = ids.join(",");

        onFilterChange({ userId: userIdStr });
        // console.log("onAgentChange ids userId:", { userId: ids });
        // onFilterChange({ userId: ids });
      } catch (e) {}
    }

    // Always update child response too
    // fetchReminders(
    //   currentViewStart || undefined,
    //   currentViewEnd || undefined,
    //   search,
    //   filterDate,
    //   ids,
    //   selectedReminderTypeValue ?? undefined,
    //   selectedStatus ?? undefined
    // );
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Card className="p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 font-medium mb-1">
                Total Reminders
              </p>
              <p className="text-2xl font-bold text-slate-800">
                {totalReminders}
              </p>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg">
              <Lucide icon="Calendar" className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 font-medium mb-1">
                Due Today
              </p>
              <p className="text-2xl font-bold text-slate-800">
                {dueTodayCount}
              </p>
            </div>
            <div className="p-2 bg-orange-100 rounded-lg">
              <Lucide icon="Clock" className="w-5 h-5 text-orange-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-medium mb-1">Overdue</p>
              <p className="text-2xl font-bold text-red-600">{overdueCount}</p>
            </div>
            <div className="p-2 bg-red-100 rounded-lg">
              <Lucide icon="AlertTriangle" className="w-5 h-5 text-red-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium mb-1">
                Completed
              </p>
              <p className="text-2xl font-bold text-green-600">
                {completedCount}
              </p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg">
              <Lucide icon="CheckCircle" className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Search and Filter Bar - Match Calendar View */}
      <Card className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-y-2 p-5">
        <div className="flex flex-col sm:flex-row gap-2  items-start">
          <div
            className="flex items-center  border border-slate-300 rounded-lg shadow-sm sm:w-56 bg-white px-1.5 max-h-[40px]  "
            // className="flex items-center transition duration-200 ease-in-out w-full text-sm border-slate-300/100 shadow-sm rounded-md m"
          >
            <Lucide
              icon="Search"
              // className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
              className=""
            />
            <FormInput
              type="text"
              value={currentSearch}
              placeholder="Search..."
              // className="pl-9 sm:w-64 rounded-[0.5rem]"
              className="  border-none shadow-none focus:ring-0 focus:border-none focus:placeholder:text-slate-400 "
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
          <div>
            <TailwindDatePicker
              value={filterDate}
              onChange={(value) => {
                setFilterDate(value);
                if (onDateRangeChange) {
                  onDateRangeChange(value);
                }
              }}
              classNames={{
                input: () =>
                  "max-h-[38px] transition duration-200 ease-in-out w-full text-sm border-slate-300/100 shadow-sm !rounded-md placeholder:text-slate-400/90 focus:ring-primary focus:ring-opacity-20 focus:border-none focus:ring-4",
              }}
              placeholder="Select date range"
              separator="-"
              inputId="date-picker-list"
              showFooter={true}
            />
          </div>

          {/* Reminder Type Filter (same as calendar) */}
          <div className="relative">
            <ReactSelect
              options={[
                { label: "All Types", value: "" },
                { label: "Follow Up", value: "follow_up" },
                { label: "Meeting", value: "meeting" },
                { label: "Call", value: "call" },
                { label: "Email", value: "email" },
              ]}
              placeholder={"All Types"}
              value={(() => {
                if (!currentFilters.reminderType) return null;
                return (
                  [
                    { label: "All Types", value: "" },
                    { label: "Follow Up", value: "follow_up" },
                    { label: "Meeting", value: "meeting" },
                    { label: "Call", value: "call" },
                    { label: "Email", value: "email" },
                  ].find((o) => o.value === currentFilters.reminderType) || null
                );
              })()}
              containerClassName={"min-w-[8rem] "}
              onChange={(selected) => {
                const val = (selected as any) || null;
                try {
                  onFilterChange({
                    reminderType: val?.value || undefined,
                    status: currentFilters?.status,
                  });
                } catch (e) {}
              }}
              className="min-w-36"
            />
          </div>
          {/* Agent Filter */}
          <div className="relative">
            {/* <ReactSelect
              options={[
                { label: "All Users", value: "" },
                ...(agentOptions?.map((u: any) => ({
                  label: `${
                    u?.name || u?.raw?.name || u?.raw?.first_name || ""
                  } ${u?.raw?.last_name || u?.raw?.lastName || ""}`.trim(),
                  value: u?.value ?? u?.id ?? "",
                  raw: u,
                })) ?? []),
              ]}
              placeholder={"All Users"}
              value={(() => {
                if (!currentFilters.userId) {
                  return { label: "All Users", value: "" };
                }
                const vals = String(currentFilters.userId)
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean);
                if (vals.length === 0) {
                  return { label: "All Users", value: "" };
                }
                return agentOptions.filter((o) => vals.includes(`${o.value}`));
              })()}
              containerClassName={"min-w-[8rem] ml-3"}
              onChange={(selected) => {
                const sel = (selected as any) || [];
                try {
                  if (!sel || sel.length === 0) {
                    onFilterChange({
                      ...(currentFilters || {}),
                      userId: undefined,
                    });
                    return;
                  }
                  const hasAll = sel.some(
                    (s: any) => s.value === "" || s.value == null
                  );
                  if (hasAll) {
                    onFilterChange({
                      ...(currentFilters || {}),
                      userId: undefined,
                    });
                    return;
                  }
                  const ids = sel
                    .map((s: any) => s.value)
                    .filter((v: any) => v !== "" && v != null);
                  onFilterChange({
                    ...(currentFilters || {}),
                    userId: ids.length === 1 ? ids[0] : ids.join(","),
                  });
                } catch (e) {}
              }}
              className="min-w-36"
            /> */}

            {/* <ReactSelect
              isMulti
              options={[
                { label: "All Users", value: "" },
                ...(agentOptions?.map((u: any) => ({
                  label: `${
                    u?.name || u?.raw?.name || u?.raw?.first_name || ""
                  } ${u?.raw?.last_name || u?.raw?.lastName || ""}`.trim(),
                  value: u?.value ?? u?.id ?? "",
                  raw: u,
                })) ?? []),
              ]}
              placeholder="All Users"
              value={(() => {
                // Prefer selectedAgentId; fallback to filters for safety
                const selected = selectedAgentId ?? currentFilters.userId;
                if (!selected) return { label: "All Users", value: "" };

                const found = agentOptions?.find(
                  (o: any) => `${o.value}` === `${selected}`
                );
                return found
                  ? { label: found.label, value: found.value }
                  : { label: "All Users", value: "" };
              })()}
              onChange={(selected: any) => {
                // Reset to All Users
                if (!selected || selected.value === "") {
                  onFilterChange({
                    ...(currentFilters || {}),
                    userId: undefined,
                  });
                  return;
                }
                // Set a specific user
                onFilterChange({
                  ...(currentFilters || {}),
                  userId: selected.value,
                });
              }}
              className="min-w-36"
              containerClassName="min-w-[8rem] ml-3"
              // Force a clean remount when the selection clears/changes
              key={`agent-${String(selectedAgentId ?? "")}`}
            /> */}

            <Select
              isMulti
              options={agentOptions}
              value={agentOptions?.filter(
                (opt) =>
                  Array.isArray(selectedAgentList) &&
                  selectedAgentList.includes(opt.value.toString()) // no need Number()
              )}
              onChange={(selected) => {
                const selectedArr = Array.isArray(selected) ? selected : [];

                // Extract values as (number | string)[]
                const ids = selectedArr.map((o) => o.value.toString());
                console.log("idsids", ids);
                setSelectedAgentList(ids); // local state
                onAgentChange(ids); // pass to parent
              }}
              placeholder="All Users"
              styles={{
                control: (provided, state) => ({
                  ...provided,

                  boxShadow: state.isFocused ? "0 0 0 1px #cbd5e1" : "none",

                  "&:focus-within": { borderColor: "inherit" },
                  borderRadius: "0.375rem",
                }),
              }}
              className="min-w-36 rounded-2xl"
            />
          </div>
          {/* Status Filter (same as calendar) */}
          <div className="relative">
            <ReactSelect
              options={[
                { label: "All Status", value: "" },
                { label: "Completed", value: "all_completed" },
                { label: "Snoozed", value: "snoozed" },
                { label: "Overdue", value: "overdue" },
                { label: "Pending", value: "pending" },
              ]}
              placeholder={"All Status"}
              value={(() => {
                if (!currentFilters.status) return null;
                return (
                  [
                    { label: "All Status", value: "" },
                    { label: "Completed", value: "all_completed" },
                    { label: "Snoozed", value: "snoozed" },
                    { label: "Overdue", value: "overdue" },
                    { label: "Pending", value: "pending" },
                  ].find((o) => o.value === currentFilters.status) || null
                );
              })()}
              containerClassName={"min-w-[7rem] "}
              onChange={(selected) => {
                const val = (selected as any) || null;
                try {
                  onFilterChange({
                    reminderType: currentFilters?.reminderType,
                    status: val?.value || undefined,
                  });
                } catch (e) {}
              }}
              className="min-w-36"
            />
          </div>
          <div className=" flex items-center gap-3">
            {/* Sort By selector using ReactSelect */}
            <div className="min-w-[9rem]">
              <ReactSelect
                options={[
                  { label: "Created At", value: "created_at" },
                  { label: "Remind On", value: "remind_on" },
                ]}
                placeholder={"Sort By"}
                value={(() => {
                  return (
                    [
                      { label: "Created At", value: "created_at" },
                      { label: "Remind On", value: "remind_on" },
                    ].find((o) => o.value === sortBy) || null
                  );
                })()}
                onChange={(selected) => {
                  const v = (selected as any)?.value as
                    | "created_at"
                    | "remind_on"
                    | undefined;
                  if (v && v !== sortBy) onSortByChange(v);
                }}
                className="min-w-36"
                containerClassName="min-w-[9rem]"
              />
            </div>

            {/* Asc/Desc icon toggle */}
            <button
              className="px-1.5 py-2 rounded-md  border border-slate-300 "
              onClick={() => {
                onSortOrderToggle();
              }}
              title={sortOrder === "asc" ? "Ascending" : "Descending"}
            >
              {sortOrder === "asc" ? (
                <div className="flex items-center text-sky-800">
                  <Lucide icon="ArrowUp" className="w-4 h-4 mr-1" />
                  <div className="flex flex-col leading-none text-[10px] font-bold">
                    <span>Z</span>
                    <span>A</span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center text-sky-800">
                  <Lucide icon="ArrowDown" className="w-4 h-4 mr-1" />
                  <div className="flex flex-col leading-none text-[10px] font-bold">
                    <span>A</span>
                    <span>Z</span>
                  </div>
                </div>
              )}
            </button>
          </div>

          <div className="flex flex-row justify-center items-center">
            {(currentSearch ||
              selectedAgentList.length > 0 ||
              currentFilters.status ||
              currentFilters.reminderType ||
              selectedAgentId ||
              (filterDate && filterDate.startDate) ||
              (filterDate && filterDate.endDate)) && (
              <Button
                onClick={() => {
                  // Trigger any UI effect or parent notification
                  setClearFiltersListTrigger(true);

                  // Reset all filters
                  onFilterChange({
                    reminderType: "",
                    status: "",
                    userId: [], // For ReactSelect, empty array = no selection
                  });

                  // Clear selected agents
                  setSelectedAgentList([]);

                  // Reset date filter (optional, uncomment if needed)
                  // const newDate = {
                  //   startDate: moment().startOf("month").toDate(),
                  //   endDate: moment().endOf("month").toDate(),
                  // };
                  // setFilterDate(newDate);

                  // Notify parent about date reset
                  // try {
                  //   if (onDateRangeChange) {
                  //     onDateRangeChange(newDate as any);
                  //   }
                  // } catch (e) {
                  //   console.error("Failed to reset date range", e);
                  // }
                }}
                size="sm"
                className=" border-slate-400 py-2.5"
                variant="outline-secondary"
              >
                <Lucide
                  icon="FilterX"
                  className="text-slate-600 mr-2 w-4 h-4"
                />
                Clear
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Reminders Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="text-slate-600">Loading reminders...</span>
            </div>
          </div>
        ) : error ? (
          <div className="p-6 text-center border-red-200 bg-red-50">
            <div className="flex items-center justify-center space-x-2">
              <Lucide icon="AlertCircle" className="w-4 h-4 text-red-600" />
              <span className="text-red-600">{error}</span>
              <Button
                onClick={onRetry}
                size="sm"
                variant="outline-secondary"
                className="ml-auto"
              >
                <Lucide icon="RotateCcw" className="w-4 h-4 mr-1" />
                Retry
              </Button>
            </div>
          </div>
        ) : events.length === 0 ? (
          <div className="p-8 text-center">
            <Lucide
              icon="Calendar"
              className="w-12 h-12 text-slate-400 mx-auto mb-3"
            />
            <h3 className="text-lg font-medium text-slate-900 mb-1">
              No reminders found
            </h3>
            <p className="text-slate-600">
              There are no reminders for the selected criteria.
            </p>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
              <div className="grid grid-cols-12 gap-4 text-sm font-medium text-slate-700">
                <div className="col-span-2">Title</div>
                <div className="col-span-2 text-center">Reminder Type</div>
                <div className="col-span-2 text-center">Assigned To</div>
                <div className="col-span-1 text-center">Assigned By</div>
                <div className="col-span-1 text-center">Query</div>
                <div className="col-span-2 text-center">Due On</div>
                <div className="col-span-1 text-center">Status</div>
                <div className="col-span-1 text-center">Action</div>
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-slate-200">
              {paginatedEvents.map((event) => (
                <div
                  key={event.id}
                  className="px-6 py-4 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => onEventClick(event)}
                >
                  <div className="grid grid-cols-12 gap-4 items-center">
                    {/* Title */}
                    <div className="col-span-2">
                      <h4 className="font-medium text-slate-900 mb-1">
                        {event.title}
                      </h4>
                      {event.status === "completed" &&
                      event.assignments &&
                      event.assignments[0]?.completed_at ? (
                        <p className="text-xs text-slate-500">
                          Marked Completed on{" "}
                          {moment(event.assignments[0].completed_at).format(
                            "DD/MM/YYYY"
                          )}
                        </p>
                      ) : (
                        <p className="text-xs text-slate-500 line-clamp-1">
                          {event.description || ""}
                        </p>
                      )}
                    </div>

                    {/* Reminder Type */}
                    <div className="col-span-2 text-center">
                      <div className="text-sm text-slate-900">
                        {(event as any).reminderData?.reminder_type_text ||
                          (event.reminderType || "").replace("_", " ") ||
                          "-"}
                      </div>
                    </div>

                    {/* Assigned To */}
                    <div className="col-span-2 text-center">
                      <div className="text-sm text-slate-900">
                        {event.assignedTo || "Unassigned"}
                      </div>
                    </div>

                    {/* Assigned By */}
                    <div className="col-span-1 text-center">
                      <div className="text-sm text-slate-900">
                        {event.createdFor || "-"}
                      </div>
                    </div>

                    {/* Query */}
                    <div className="col-span-1 text-center">
                      <div className="text-sm">
                        {(event as any).reminderData?.query_record?.query_no ? (
                          <span
                            className="text-slate-900 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEventClick(event);
                            }}
                          >
                            {
                              (event as any).reminderData?.query_record
                                ?.query_no
                            }
                          </span>
                        ) : (
                          <span className="text-slate-500">N/A</span>
                        )}
                      </div>
                    </div>

                    {/* Due On */}
                    <div className="col-span-2 text-center">
                      <div className="text-sm text-slate-900">
                        {moment(event.start).format("DD/MM/YYYY, HH:mm:ss")}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="col-span-1 text-center">
                      {getStatusBadge(event.status || "pending")}
                    </div>

                    {/* Action */}

                    {event.status !== "completed" && (
                      <div className="col-span-1 text-center">
                        <div className="flex justify-center space-x-2">
                          {checkPermission(reminderAPIUrls.updateReminder) &&
                          !isSuperAdmin() ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (event.query && onEditReminder) {
                                  onEditReminder(event.query);
                                }
                              }}
                              className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Lucide icon="Pencil" className="w-4 h-4" />
                            </button>
                          ) : null}

                          {checkPermission(reminderAPIUrls.deleteReminder) &&
                          !isSuperAdmin() ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (event.query && onDeleteReminder) {
                                  onDeleteReminder(event.query);
                                }
                              }}
                              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Lucide icon="Trash2" className="w-4 h-4" />
                            </button>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="bg-slate-50 px-6 py-4 border-t border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="p-2 text-slate-400 hover:text-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Lucide icon="ChevronLeft" className="w-4 h-4" />
                  </button>

                  <div className="flex space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-1 rounded text-sm ${
                            currentPage === page
                              ? "bg-primary text-white"
                              : "text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="p-2 text-slate-400 hover:text-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Lucide icon="ChevronRight" className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex items-center space-x-4">
                  <span className="text-sm text-slate-600">
                    Showing {startIndex + 1}-{Math.min(endIndex, events.length)}{" "}
                    of {events.length}
                  </span>
                  <FormSelect
                    value={itemsPerPage.toString()}
                    className="w-auto"
                    onChange={(e) => {
                      // Update items per page and reset to first page
                      const v =
                        Number((e.target as HTMLSelectElement).value) || 10;
                      setItemsPerPage(v);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                  </FormSelect>
                </div>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

function Component({
  users,
  fetchUsers,
}: {
  users?: any[];
  fetchUsers?: (payload?: any) => void;
}) {
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ReminderEvent | null>(
    null
  );
  const [filterDate, setFilterDate] = useState<DateValueType>({
    startDate: "",
    endDate: "",
  });
  console.log(selectedEvent, "selectedEvent");

  // derive query_no from possible locations so UI shows human-friendly query number
  const displayedQueryNo = selectedEvent
    ? (selectedEvent as any)?.reminderData?.query_record?.query_no ?? null
    : null;

  const [showEventDetail, setShowEventDetail] = useState(false);
  const [showSnoozeModal, setShowSnoozeModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  // filer State management

  const [selectedAgent, setSelectedAgent] = useState<(number | string)[]>([]);
  const [selectedAgentList, setSelectedAgentList] = useState<
    (number | string)[]
  >([]);
  const [search, setSearch] = useState<string>("");

  // Store human-readable label in `selectedReminderType` for display
  // and keep the actual API value in `selectedReminderTypeValue`.
  const [selectedReminderType, setSelectedReminderType] = useState<
    string | undefined
  >(undefined);
  const [selectedReminderTypeValue, setSelectedReminderTypeValue] = useState<
    string | undefined
  >();
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(
    undefined
  );

  // Keep a human-readable label for selected status for display while
  // the API uses `selectedStatus` (the value).
  const [selectedStatusLabel, setSelectedStatusLabel] = useState<
    string | undefined
  >(undefined);

  // listView Filter State management
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch agents for filters (used by List View agent selector)
  useEffect(() => {
    try {
      if (fetchUsers) fetchUsers({ type: 1 } as any);
    } catch {}
  }, []);

  const [reminderToDelete, setReminderToDelete] = useState<string>("");
  const [snoozeTime, setSnoozeTime] = useState("15 Minutes");
  const [completionRemarks, setCompletionRemarks] = useState("");

  // Listen for external requests to open the complete or snooze modal (e.g., from ReminderQueuePanel)
  useEffect(() => {
    function handlerComplete(e: Event) {
      try {
        const ce = e as CustomEvent;
        const payload = ce.detail;
        if (payload && payload.item) {
          const item = payload.item;
          const remEvent: any = {
            id: item.reminder?.id || item.id,
            title: item.reminder?.title || item.title,
            description: item.reminder?.description || item.description,
            assignments: item.reminder?.assignments || item.assignments || [],
            reminder: item.reminder || null,
            query:
              item.reminder?.id || item.query || item.reminder_id || undefined,
          };
          setSelectedEvent(remEvent);
          setShowEventDetail(false);
          setTimeout(() => setShowCompleteModal(true), 100);
        }
      } catch (err) {
        console.error("open-complete-modal handler error", err);
      }
    }

    function handlerSnooze(e: Event) {
      try {
        const ce = e as CustomEvent;
        const payload = ce.detail;
        if (payload && payload.item) {
          const item = payload.item;
          const remEvent: any = {
            id: item.reminder?.id || item.id,
            title: item.reminder?.title || item.title,
            description: item.reminder?.description || item.description,
            assignments: item.reminder?.assignments || item.assignments || [],
            reminder: item.reminder || null,
            query:
              item.reminder?.id || item.query || item.reminder_id || undefined,
          };
          setSelectedEvent(remEvent);
          setShowEventDetail(false);
          setTimeout(() => setShowSnoozeModal(true), 100);
        }
      } catch (err) {
        console.error("open-snooze-modal handler error", err);
      }
    }

    window.addEventListener(
      "open-complete-modal",
      handlerComplete as EventListener
    );
    window.addEventListener(
      "open-snooze-modal",
      handlerSnooze as EventListener
    );
    return () => {
      window.removeEventListener(
        "open-complete-modal",
        handlerComplete as EventListener
      );
      window.removeEventListener(
        "open-snooze-modal",
        handlerSnooze as EventListener
      );
    };
  }, []);

  // API state management
  const [apiReminders, setApiReminders] = useState<ReminderEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [currentViewStart, setCurrentViewStart] = useState<Date | null>(null);

  console.log(currentViewStart, "currentViewStart");

  const [currentViewEnd, setCurrentViewEnd] = useState<Date | null>(null);

  console.log(currentViewEnd, "currentViewEnd");
  const [currentSearch, setCurrentSearch] = useState<string>("");
  const [currentFilters, setCurrentFilters] = useState<{
    reminderType?: string;
    status?: string;
    userId?: string | number | (string | number)[] | undefined;
  }>({
    userId: [], // default as empty array
  });
  const [selectedAgentId, setSelectedAgentId] = useState<
    string | number | undefined
  >(undefined);
  const [sortBy, setSortBy] = useState<"created_at" | "remind_on" | undefined>(
    "created_at"
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const sortMenuRef = useRef<HTMLDivElement | null>(null);

  // Close sort dropdown on outside click
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      try {
        if (!sortMenuOpen) return;
        const el = sortMenuRef.current;
        if (el && !el.contains(e.target as Node)) {
          setSortMenuOpen(false);
        }
      } catch {}
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [sortMenuOpen]);

  // Show a page-level loader when creation/refresh takes longer than a
  // short threshold to avoid flicker for fast operations.
  useEffect(() => {
    const SPINNER_DELAY_MS = 600; // only show spinner if op > 600ms
    let creatingTimer: number | null = null;
    let calendarTimeout: number | null = null;

    const handleCreating = () => {
      // Start a delayed timer to show the loader to prevent flicker
      if (creatingTimer) {
        clearTimeout(creatingTimer);
      }
      creatingTimer = window.setTimeout(() => {
        setLoading(true);
        creatingTimer = null;
      }, SPINNER_DELAY_MS) as unknown as number;
    };

    const handleCreated = (e: Event) => {
      // Operation finished — cancel pending timer and ensure loader clears
      try {
        if (creatingTimer) {
          clearTimeout(creatingTimer as number);
          creatingTimer = null;
        }

        // Force loading off after a short delay to ensure UI updates
        if (calendarTimeout) {
          clearTimeout(calendarTimeout);
        }
        calendarTimeout = window.setTimeout(() => {
          setLoading(false);
          calendarTimeout = null;
        }, 100) as unknown as number;
      } catch (err) {
        // If timer cleanup fails, force loading off
        setLoading(false);
      }
    };

    window.addEventListener(
      "reminders:creating",
      handleCreating as EventListener
    );
    window.addEventListener(
      "reminders:created",
      handleCreated as EventListener
    );

    return () => {
      try {
        if (creatingTimer) clearTimeout(creatingTimer as number);
      } catch (err) {}
      window.removeEventListener(
        "reminders:creating",
        handleCreating as EventListener
      );
      window.removeEventListener(
        "reminders:created",
        handleCreated as EventListener
      );
    };
  }, []);
  const [viewMode, setViewMode] = useState<"calendar" | "list">("calendar");
  const [editMode, setEditMode] = useState(false);
  const [editReminderId, setEditReminderId] = useState<string>("");
  const [editReminderData, setEditReminderData] = useState<any>(null);
  const [newReminder, setNewReminder] = useState({
    title: "",
    description: "",
    date: new Date(),
    priority: "medium",
    assignedTo: "",
  });

  const [clearFiltersTrigger, setClearFiltersTrigger] = useState(false);
  const [clearFiltersListTrigger, setClearFiltersListTrigger] = useState(false);

  // Keep agent selection in sync when switching views so filters persist
  const handleViewModeChange = (mode: "calendar" | "list") => {
    try {
      setViewMode(mode);
      // Clear search and filters on view change per request
      setSearch("");
      setCurrentSearch("");
      setSelectedAgent([]);
      setSelectedAgentList([]);
      setCurrentFilters({
        reminderType: undefined,
        status: undefined,
        userId: [],
      });
      setCurrentPage(1);

      // Keep date range intact; just refetch with cleared filters
      const [apiStart, apiEnd] = toApiRange(currentViewStart, currentViewEnd);
      fetchReminders(
        apiStart,
        apiEnd,
        "",
        undefined,
        undefined,
        [],
        sortBy,
        undefined,
        sortOrder
      );
    } catch (e) {
      // noop
    }
  };

  // Fetch reminders from API
  // Helper: convert possibly-exclusive FullCalendar end to an inclusive API range
  const toApiRange = (start?: Date | null, end?: Date | null) => {
    const s = start ? moment(start).startOf("day").toDate() : undefined;
    const e = end
      ? moment(end).subtract(1, "day").endOf("day").toDate()
      : undefined;
    return [s, e] as [Date | undefined, Date | undefined];
  };

  const fetchReminders = async (
    startDate?: Date,
    endDate?: Date,
    searchTerm?: string,
    reminderType?: string,
    status?: string,
    userId?: string | number | Array<string | number> | undefined,
    sortByField?: "created_at" | "remind_on",
    sortBy?: string | undefined,
    sortOrder?: string,
    sortOrderDir?: "asc" | "desc"
  ) => {
    console.log("startDate----->", startDate, "endDate---->", endDate);
    const start = startDate || moment().startOf("month").toDate();
    const end = endDate || moment().endOf("month").toDate();

    const startDateStr = moment(start).format("YYYY-MM-DD");
    const endDateStr = moment(end).format("YYYY-MM-DD");

    if (!loading) setLoading(true);
    setError(null);
    const fetchStart = Date.now();
    console.log(
      "fetchReminders start",
      fetchStart,
      startDate,
      endDate,
      searchTerm,
      reminderType,
      status,
      userId
    );

    try {
      console.log(
        "Main Reminders page - Fetching reminders for date range:",
        startDateStr,
        "to",
        endDateStr,
        "with search:",
        searchTerm,
        "type:",
        reminderType,
        "status:",
        status
      );
      const response = await fetchReminderCalendarService(
        startDateStr,
        endDateStr,
        searchTerm,
        reminderType,
        status,
        userId as string | number | (string | number)[] | undefined,
        sortByField ?? (sortBy as "created_at" | "remind_on" | undefined),
        sortOrderDir ?? (sortOrder as "asc" | "desc" | undefined)
      );
      console.log("Main Reminders page - API Response:", response);

      if (response && Array.isArray(response)) {
        console.log(
          "✅ Main Reminders page - Received",
          response.length,
          "reminders from API"
        );
        // Transform API response to match ReminderEvent interface
        const transformedReminders: ReminderEvent[] = response.map(
          (apiEvent: any) => {
            const reminderData = apiEvent.extendedProps?.reminder;

            console.log(reminderData, "reminderData in transformation");

            // Get assigned users with their status
            const assignedUsers = reminderData?.assigned_users || [];
            const assignments = reminderData?.assignments || [];

            // Build assignedTo string from assigned users
            const assignedToNames =
              assignedUsers
                .map((user: any) =>
                  `${user.name} ${user.last_name || ""}`.trim()
                )
                .join(", ") || "Unassigned";

            // Determine overall status from assignments; fallback to API overall_status
            let overallStatus: "pending" | "completed" | "overdue" | "snoozed" =
              "pending";
            if (assignments.length > 0) {
              const hasOverdue = assignments.some(
                (assignment: any) => assignment.status === "overdue"
              );
              const hasCompleted = assignments.some(
                (assignment: any) => assignment.status === "completed"
              );
              const hasPending = assignments.some(
                (assignment: any) => assignment.status === "pending"
              );

              if (hasOverdue) {
                overallStatus = "overdue";
              } else if (hasCompleted && !hasPending && !hasOverdue) {
                overallStatus = "completed";
              }
            } else if (reminderData?.overall_status) {
              const s = String(reminderData.overall_status).toLowerCase();
              if (s === "all_completed" || s === "completed")
                overallStatus = "completed";
              else if (s === "overdue") overallStatus = "overdue";
              else if (s === "snoozed") overallStatus = "snoozed";
            }

            // Determine priority based on reminder_before_minutes
            let priority: "high" | "medium" | "low" = "medium";
            const reminderBefore = reminderData?.remind_before_minutes || 0;
            if (reminderBefore <= 15) {
              priority = "high";
            } else if (reminderBefore >= 60) {
              priority = "low";
            }

            // Use colors from API response or default based on status
            let backgroundColor = apiEvent.backgroundColor;
            let borderColor = apiEvent.borderColor;
            let textColor = apiEvent.textColor || "white";

            // Override colors for overdue items
            if (overallStatus === "overdue") {
              backgroundColor = "#dc2626"; // Red for overdue
              borderColor = "#dc2626";
              textColor = "white";
            } else if (overallStatus === "completed") {
              backgroundColor = "#10b981"; // Green for completed
              borderColor = "#10b981";
              textColor = "white";
            } else if (overallStatus === "snoozed") {
              backgroundColor = "#f59e0b"; // Amber for snoozed
              borderColor = "#f59e0b";
              textColor = "white";
            }

            return {
              id: apiEvent.id,
              title: apiEvent.title,
              start: apiEvent.start,
              end: apiEvent.end,
              backgroundColor,
              borderColor,
              textColor,
              description: apiEvent.description,
              assignedTo: assignedToNames,
              priority,
              query: reminderData?.id?.toString(),
              createdFor: reminderData?.created_by?.name
                ? `${reminderData.created_by.name} ${
                    reminderData.created_by.last_name || ""
                  }`.trim()
                : "Unknown",
              scheduledTime: moment
                .utc(reminderData?.remind_on)
                .format("DD/MM/YYYY 🕐 HH:mm"),
              // Additional data for event details
              reminderType: reminderData?.reminder_type as
                | "call"
                | "follow_up"
                | "payment"
                | "booking"
                | undefined,
              status: overallStatus,
              reminderBeforeMinutes: reminderData?.remind_before_minutes,
              assignments: assignments,
              assignedUsers: assignedUsers,
              // Add reminder data so it's available in calendar component as extendedProps.reminder
              reminder: reminderData,
              reminderData: reminderData, // Also keep as reminderData for backward compatibility
            };
          }
        );

        console.log(
          "🎯 Main Reminders page - Setting state with",
          transformedReminders.length,
          "transformed reminders"
        );
        setApiReminders(transformedReminders);
      } else {
        console.log("❌ API response is not an array, clearing reminders");
        setApiReminders([]);
      }
    } catch (err: any) {
      console.error("Error fetching reminders:", err);
      setError("Failed to load reminders");
      setApiReminders([]);
    } finally {
      console.log(
        "fetchReminders end",
        Date.now(),
        "durationMs:",
        Date.now() - fetchStart
      );

      setLoading(false);
    }
  };

  // Trigger refresh when reminders are created
  const triggerRefresh = () => {
    console.log("🔄 Triggering reminder refresh...");
    setRefreshTrigger((prev) => prev + 1);

    // Add a small delay to ensure backend has completed processing
    // the create/update operation before we fetch the updated list
    setTimeout(() => {
      console.log("📡 Fetching updated reminders after create/update");
      // Always attempt to refresh the reminders. If the calendar range is
      // available use it, otherwise let fetchReminders fall back to its
      // default range by passing undefined.
      const [apiStart, apiEnd] = toApiRange(currentViewStart, currentViewEnd);
      fetchReminders(
        apiStart,
        apiEnd,
        currentSearch,
        currentFilters.reminderType,
        currentFilters.status,
        selectedAgent as number[] | string[],
        sortBy,
        sortOrder
      );
    }, 200); // 200ms delay to allow backend to process
  };

  // Initial load
  useEffect(() => {
    if (clearFiltersTrigger) {
      setCurrentViewStart(null);
      setCurrentViewEnd(null);
      setSearch("");
      setSelectedAgent([]);

      setSelectedReminderType(undefined);
      setSelectedReminderTypeValue(undefined);

      setSelectedStatus(undefined);
      setSelectedStatusLabel(undefined);
    }
    if (clearFiltersListTrigger) {
      setFilterDate({
        startDate: null,
        endDate: null,
      });
      setCurrentSearch("");
      setCurrentPage(1);
      setSortBy(undefined);
      // setCurrentFilters({
      //   reminderType: "",
      //   status: "",
      //   userId: undefined,
      // });
      // setCurrentFilters((prev) => ({
      //   ...prev,
      //   userId: undefined, // VERY IMPORTANT
      // }));

      // 👇 Reset local agent selections (if any)
      // setSelectedAgentId("");
      setSelectedAgentList([]);
    }
    console.log(
      "Initial load - fetching reminders useEffect triggered",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      selectedAgent as number[] | string[],
      sortBy,
      sortOrder
    );

    console.log("Initial load - fetching reminders");
    fetchReminders(
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      selectedAgent as number[] | string[],
      sortBy,
      sortOrder
    );
    return () => {
      setClearFiltersTrigger(false);
      setClearFiltersListTrigger(false);
    };
  }, [
    // selectedAgentId,
    // sortBy,
    // sortOrder,
    clearFiltersTrigger,
    clearFiltersListTrigger,
  ]);

  // Re-fetch when location.search changes (e.g., /reminders?refresh=timestamp)
  const location = useLocation();
  useEffect(() => {
    console.log(
      "ReminderCalendar useEffect - location.search changed:",
      location.search
    );
    try {
      console.log("Reminders page - location.search changed:", location.search);
      const [apiStart, apiEnd] = toApiRange(currentViewStart, currentViewEnd);
      fetchReminders(
        apiStart,
        apiEnd,
        currentSearch,
        currentFilters.reminderType,
        currentFilters.status,
        selectedAgent as number[] | string[],
        sortBy,
        undefined,
        sortOrder
      );
    } catch (err) {
      console.error("Error re-fetching reminders on location change", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Remove sample events - only use API data
  const [reminders, setReminders] = useState<ReminderEvent[]>([]);

  const handleEventClick = (event: ReminderEvent) => {
    console.log("handleEventClick called with:", event);
    console.log("Event ID:", event.id, "Event Query:", event.query);

    // Always show modal on event click (like previous behavior)
    setSelectedEvent(event);
    setShowEventDetail(true);
  };

  // New function to navigate to detail page
  const handleViewDetailPage = (event: ReminderEvent) => {
    const reminderId = event.query || event.id;
    if (reminderId) {
      console.log(
        "Navigating to detail page:",
        `/reminders-details/${reminderId}`
      );
      navigate(`/reminders-details/${reminderId}`);
    } else {
      console.log("No ID found for navigation");
    }
  };

  const handleDateSelect = (date: Date) => {
    setNewReminder({
      ...newReminder,
      date: date,
    });
    setShowAddModal(true);
  };

  const handleAddReminder = () => {
    setEditMode(false);
    setEditReminderId("");
    setEditReminderData(null);
    setShowAddModal(true);
  };

  const handleEditReminder = async (reminderId: string) => {
    try {
      setLoading(true);
      const response = await fetchReminderByIdService(reminderId);

      if (response && response.status) {
        setEditMode(true);
        setEditReminderId(reminderId);
        setEditReminderData(response.result);
        setShowAddModal(true);
      } else {
        alert("Failed to fetch reminder details. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching reminder details:", error);
      alert("Failed to fetch reminder details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReminder = () => {
    if (newReminder.title.trim()) {
      const priorityColors = {
        high: { bg: "#ef4444", border: "#ef4444" },
        medium: { bg: "#f59e0b", border: "#f59e0b" },
        low: { bg: "#10b981", border: "#10b981" },
      };

      const colors =
        priorityColors[newReminder.priority as keyof typeof priorityColors];

      const reminder: ReminderEvent = {
        id: Date.now().toString(),
        title: newReminder.title,
        start: moment(newReminder.date).format("YYYY-MM-DD"),
        backgroundColor: colors.bg,
        borderColor: colors.border,
        textColor: "white",
        description: newReminder.description,
        assignedTo: newReminder.assignedTo,
        priority: newReminder.priority as "high" | "medium" | "low",
      };

      setReminders([...reminders, reminder]);
      setNewReminder({
        title: "",
        description: "",
        date: new Date(),
        priority: "medium",
        assignedTo: "",
      });
      setShowAddModal(false);
    }
  };

  // Convert snooze time text to minutes
  const convertSnoozeTimeToMinutes = (snoozeText: string) => {
    switch (snoozeText) {
      case "15 Minutes":
        return 15;
      case "30 Minutes":
        return 30;
      case "1 Hour":
        return 60;
      case "2 Hours":
        return 120;
      case "4 Hours":
        return 240;
      case "1 Day":
        return 1440;
      case "2 Days":
        return 2880;
      case "1 Week":
        return 10080;
      default:
        return 30;
    }
  };

  // Handle reminder completion
  const handleCompleteReminder = async () => {
    if (!selectedEvent) return;

    try {
      // For API reminders, use the reminder ID directly
      const reminderId = selectedEvent.id;

      await completeReminderService({
        id: reminderId,
        completion_remarks: completionRemarks,
        callback: (hasError: boolean, data: any) => {
          if (!hasError) {
            triggerRefresh();
            setCompletionRemarks("");
            setShowCompleteModal(false);
            setShowEventDetail(false);
          } else {
            console.error("Error completing reminder:", data);
          }
        },
      });
    } catch (error) {
      console.error("Error completing reminder:", error);
    }
  };

  // Handle reminder snooze
  const handleSnoozeReminder = async () => {
    if (!selectedEvent) return;

    try {
      // For API reminders, use the reminder ID directly
      const reminderId = selectedEvent.id;
      const snoozeMinutes = convertSnoozeTimeToMinutes(snoozeTime);

      await snoozeReminderService({
        id: reminderId,
        snooze_minutes: snoozeMinutes,
        callback: (hasError: boolean, data: any) => {
          if (!hasError) {
            triggerRefresh();
            setSnoozeTime("15 Minutes");
            setShowSnoozeModal(false);
            setShowEventDetail(false);
          } else {
            console.error("Error snoozing reminder:", data);
          }
        },
      });
    } catch (error) {
      console.error("Error snoozing reminder:", error);
    }
  };

  // Handle reminder deletion
  const handleDeleteReminder = (reminderId: string) => {
    setReminderToDelete(reminderId);
    setShowDeleteModal(true);
  };

  const confirmDeleteReminder = async () => {
    if (!reminderToDelete) return;

    try {
      await deleteReminderService(reminderToDelete);

      // Close any open modals
      setShowDeleteModal(false);
      setShowEventDetail(false);
      setReminderToDelete("");

      // Refresh the reminder list
      triggerRefresh();

      console.log("Reminder deleted successfully");
    } catch (error) {
      console.error("Error deleting reminder:", error);
      // You might want to show an error toast here
    }
  };

  const cancelDeleteReminder = () => {
    setShowDeleteModal(false);
    setReminderToDelete("");
  };

  const priorityOptions = [
    { value: "high", label: "High Priority" },
    { value: "medium", label: "Medium Priority" },
    { value: "low", label: "Low Priority" },
  ];

  // Build agent options from full users list if available
  const allAgentOptions = (users || []).map((u: any) => {
    const name = `${(u?.name || u?.first_name || "").toString()} ${(
      u?.last_name ||
      u?.lastName ||
      ""
    ).toString()}`.trim();
    return {
      label: name,
      value:
        u?.pylon_user_id ??
        u?.pylonId ??
        u?.pylon_id ??
        u?.external_id ??
        u?.id,
      raw: u,
      name,
    } as any;
  });

  function onClearFilter() {
    console.log("Clearing filters (calendar view)");
    // Explicitly reset all filter-related state BEFORE triggering effect
    setSearch("");
    setCurrentSearch("");
    setSelectedAgent([]);
    setSelectedReminderType(undefined);
    setSelectedReminderTypeValue(undefined);
    setSelectedStatus(undefined);
    setSelectedStatusLabel(undefined);
    setCurrentFilters({
      reminderType: undefined,
      status: undefined,
      userId: [],
    });
    // Keep current calendar visible month if already set; else use current month.
    const start = currentViewStart || moment().startOf("month").toDate();
    const end = currentViewEnd || moment().endOf("month").toDate();
    // Trigger sanitized fetch (no stale search / filters)
    fetchReminders(
      start,
      end,
      "", // empty search
      undefined,
      undefined,
      [],
      undefined,
      undefined
    );
    // Set trigger last (if other effects rely on it)
    setClearFiltersTrigger(true);
  }

  // Preserve all active filters when sorting in calendar view
  function handleCalendarSortByChange(v: "created_at" | "remind_on") {
    setSortBy(v);
    const [apiStart, apiEnd] = toApiRange(currentViewStart, currentViewEnd);
    fetchReminders(
      apiStart,
      apiEnd,
      currentSearch,
      currentFilters.reminderType,
      currentFilters.status,
      currentFilters.userId || (selectedAgent as number[] | string[]),
      v,
      undefined,
      sortOrder
    );
  }

  function handleCalendarSortOrderToggle() {
    const next = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(next);
    const [apiStart, apiEnd] = toApiRange(currentViewStart, currentViewEnd);
    fetchReminders(
      apiStart,
      apiEnd,
      currentSearch,
      currentFilters.reminderType,
      currentFilters.status,
      currentFilters.userId || (selectedAgent as number[] | string[]),
      sortBy,
      undefined,
      next
    );
  }

  return (
    <div className="grid grid-cols-12 gap-6">
      <div className="col-span-12 relative">
        {loading && (
          <div className=" z-50 pointer-events-none">
            <div className="flex items-center justify-center space-x-2  rounded px-3 py-1">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
              <span className="text-slate-600 text-sm">Loading...</span>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-4">
            <div className="flex items-center space-x-2">
              <Lucide icon="AlertCircle" className="w-5 h-5 text-red-600" />
              <span className="text-red-600">{error}</span>
              <Button
                onClick={() => {
                  const [apiStart, apiEnd] = toApiRange(
                    currentViewStart,
                    currentViewEnd
                  );
                  fetchReminders(
                    apiStart,
                    apiEnd,
                    currentSearch,
                    currentFilters.reminderType,
                    currentFilters.status,
                    selectedAgent as number[] | string[],
                    sortBy,
                    undefined,
                    sortOrder
                  );
                }}
                size="sm"
                variant="outline-secondary"
                className="ml-auto"
              >
                <Lucide icon="RotateCcw" className="w-4 h-4 mr-1" />
                Retry
              </Button>
            </div>
          </div>
        )}

        {/* View Toggle Buttons */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex bg-slate-100 rounded-lg p-1 border border-slate-200">
            <button
              className={`flex items-center px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                viewMode === "calendar"
                  ? "bg-white text-primary shadow-sm border border-slate-200"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
              }`}
              onClick={() => handleViewModeChange("calendar")}
            >
              <Lucide icon="Calendar" className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Calendar View</span>
              <span className="sm:hidden">Calendar</span>
            </button>
            <button
              className={`flex items-center px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                viewMode === "list"
                  ? "bg-white text-primary shadow-sm border border-slate-200"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
              }`}
              onClick={() => handleViewModeChange("list")}
            >
              <Lucide icon="List" className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">List View</span>
              <span className="sm:hidden">List</span>
            </button>
          </div>

          {/* View Mode Info */}
          <div className="text-sm text-slate-500 order-first sm:order-last flex items-center space-x-2">
            <div>
              {checkPermission(reminderAPIUrls.remindersExport) &&
              !isSuperAdmin() ? (
                <Button
                  className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-md shadow"
                  onClick={() => setShowExportModal(true)}
                >
                  <Lucide icon="Plus" className="w-4 h-4 mr-2" />
                  Export
                </Button>
              ) : null}
            </div>

            <span className="font-medium text-slate-700">
              {viewMode === "calendar" ? "Calendar View" : "List View"}
            </span>
            <span className="mx-1">•</span>
            <span>
              {apiReminders.length} reminder
              {apiReminders.length !== 1 ? "s" : ""}
            </span>

            {/* Sort Controls */}
          </div>
        </div>

        {/* Conditional Rendering based on view mode */}
        <div className="transition-all duration-300 ease-in-out">
          {viewMode === "calendar" ? (
            <ReminderCalendar
              key={`calendar-${refreshTrigger}`}
              events={apiReminders}
              allAgentOptions={allAgentOptions}
              onEventClick={handleEventClick}
              onDateSelect={handleDateSelect}
              onAddReminder={handleAddReminder}
              onClearFilter={onClearFilter}
              onSortByChange={handleCalendarSortByChange}
              onSortOrderToggle={handleCalendarSortOrderToggle}
              selectedAgent={selectedAgent}
              setSelectedAgent={(agents: (number | string)[]) => {
                setSelectedAgent(agents);
              }}
              selectedReminderType={selectedReminderType}
              setSelectedReminderType={setSelectedReminderType}
              selectedReminderTypeValue={selectedReminderTypeValue}
              setSelectedReminderTypeValue={setSelectedReminderTypeValue}
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              selectedStatusLabel={selectedStatusLabel}
              setSelectedStatusLabel={setSelectedStatusLabel}
              search={search}
              setSearch={setSearch}
              onViewChange={(startDate: Date, endDate: Date) => {
                console.log(
                  "Main page - View changed to:==========>",
                  startDate,
                  "to",
                  endDate
                );
                setCurrentViewStart(startDate);
                setCurrentViewEnd(endDate);
                // const [apiStart, apiEnd] = toApiRange(startDate, endDate);
                const s = startDate
                  ? moment(startDate).startOf("day").toDate()
                  : undefined;
                const e = endDate
                  ? moment(endDate).endOf("day").toDate()
                  : undefined;

                console.log("API Range:", s, e);
                // const startDate = moment(dateRange.startDate).toDate();
                //                   const endDate = moment(dateRange.endDate).toDate();
                fetchReminders(
                  s,
                  e,
                  currentSearch,
                  currentFilters.reminderType,
                  currentFilters.status,
                  selectedAgent as number[] | string[],
                  sortBy,
                  sortOrder
                );
              }}
              refreshTrigger={refreshTrigger}
              onSearchChange={(searchTerm: string) => {
                console.log("Main page - Search changed to:", searchTerm);
                setCurrentSearch(searchTerm);
                const [apiStart, apiEnd] = toApiRange(
                  currentViewStart,
                  currentViewEnd
                );
                fetchReminders(
                  apiStart,
                  apiEnd,
                  searchTerm,
                  currentFilters.reminderType,
                  currentFilters.status,
                  selectedAgent as number[] | string[],
                  sortBy,
                  undefined,
                  sortOrder
                );
              }}
              onFilterChange={(filters: {
                reminderType?: string;
                status?: string;
                userId?: string | number | Array<string | number> | undefined;
              }) => {
                console.log(
                  "Main page - Filters changed to:---------------eee----->",
                  filters
                );
                const hasUserId = Object.prototype.hasOwnProperty.call(
                  filters,
                  "userId"
                );
                // Merge without dropping existing userId when only type/status change
                setCurrentFilters((prev) => ({
                  reminderType:
                    filters.reminderType !== undefined
                      ? filters.reminderType
                      : prev.reminderType,
                  status:
                    filters.status !== undefined ? filters.status : prev.status,
                  userId: hasUserId ? filters.userId : prev.userId,
                }));
                // If child requested a specific date range (e.g., restore
                // previous range), respect it and fetch that range.
                if ((filters as any).startDate && (filters as any).endDate) {
                  try {
                    const startDate = moment(
                      (filters as any).startDate
                    ).toDate();
                    const endDate = moment((filters as any).endDate).toDate();

                    console.log(
                      "Restoring date range from child filters:",
                      startDate,
                      endDate
                    );
                    setCurrentViewStart(startDate);
                    setCurrentViewEnd(endDate);
                    console.log("filters---->", filters);
                    const [apiStart, apiEnd] = toApiRange(startDate, endDate);
                    const effectiveUserId = hasUserId
                      ? filters.userId
                      : currentFilters.userId || (selectedAgent as any);
                    fetchReminders(
                      apiStart,
                      apiEnd,
                      currentSearch,
                      filters.reminderType !== undefined
                        ? filters.reminderType
                        : currentFilters.reminderType,
                      filters.status !== undefined
                        ? filters.status
                        : currentFilters.status,
                      effectiveUserId,
                      sortBy,
                      undefined,
                      sortOrder
                    );
                    return;
                  } catch (e) {
                    console.warn(
                      "Failed to parse start/end from child filters",
                      e
                    );
                  }
                }
                // Update selectedAgentId (including clearing when undefined/null)
                // if (
                //   Object.prototype.hasOwnProperty.call(
                //     filters,
                //     "selectedAgentId"
                //   )
                // ) {
                //   setSelectedAgentId(filters.selectedAgentId ?? undefined);
                // }
                // const nextSelected = Object.prototype.hasOwnProperty.call(
                //   filters,
                //   "selectedAgentId"
                // )
                //   ? (filters.selectedAgentId as any) ?? undefined
                //   : selectedAgentId;

                // console.log(
                //   "Main page - Filters changed to:---------------eee----->",
                //   filters
                // );
                const [apiStart, apiEnd] = toApiRange(
                  currentViewStart,
                  currentViewEnd
                );
                console.log("filters----ddfsdf>", filters);
                const effectiveUserId = hasUserId
                  ? filters.userId
                  : currentFilters.userId || (selectedAgent as any);
                fetchReminders(
                  apiStart,
                  apiEnd,
                  currentSearch,
                  filters.reminderType !== undefined
                    ? filters.reminderType
                    : currentFilters.reminderType,
                  filters.status !== undefined
                    ? filters.status
                    : currentFilters.status,
                  effectiveUserId,
                  sortBy,
                  undefined,
                  sortOrder
                );
              }}
            />
          ) : (
            <ReminderListView
              events={apiReminders}
              onEventClick={handleEventClick}
              loading={loading}
              selectedAgentList={selectedAgentList}
              setSelectedAgentList={(agents: (number | string)[]) => {
                setSelectedAgentList(agents);
              }}
              error={error}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              filterDate={filterDate}
              setFilterDate={setFilterDate}
              setClearFiltersListTrigger={setClearFiltersListTrigger}
              currentSearch={currentSearch}
              currentFilters={currentFilters}
              sortBy={sortBy}
              sortOrder={sortOrder}
              agentOptions={allAgentOptions}
              selectedAgentId={selectedAgentId}
              onSortByChange={(v) => {
                setSortBy(v);
                // <CHANGE> Use filterDate instead of calendar view dates to preserve user-selected date filters
                let apiStart, apiEnd;
                if (filterDate && filterDate.startDate && filterDate.endDate) {
                  apiStart = moment(filterDate.startDate).toDate();
                  apiEnd = moment(filterDate.endDate).toDate();
                } else {
                  [apiStart, apiEnd] = toApiRange(
                    currentViewStart,
                    currentViewEnd
                  );
                }
                const effectiveUserId =
                  (currentFilters.userId as any) ||
                  (selectedAgentList as unknown as number[] | string[]) ||
                  (selectedAgent as number[] | string[]);
                console.log(
                  "Effective User ID for list view sort:",
                  apiStart,
                  apiEnd,
                  currentSearch,
                  currentFilters.reminderType,
                  currentFilters.status,
                  effectiveUserId,
                  v,
                  sortOrder
                );

                fetchReminders(
                  apiStart,
                  apiEnd,
                  currentSearch,
                  currentFilters.reminderType,
                  currentFilters.status,
                  effectiveUserId,
                  v,
                  sortOrder
                );
              }}
              onSortOrderToggle={() => {
                const next = sortOrder === "asc" ? "desc" : "asc";
                setSortOrder(next);
                // Prefer explicit list view date range selection if available
                let apiStart: Date | undefined;
                let apiEnd: Date | undefined;
                if (filterDate && filterDate.startDate && filterDate.endDate) {
                  apiStart = moment(filterDate.startDate).toDate();
                  apiEnd = moment(filterDate.endDate).toDate();
                } else {
                  [apiStart, apiEnd] = toApiRange(
                    currentViewStart,
                    currentViewEnd
                  );
                }
                const effectiveUserId =
                  (currentFilters.userId as any) ||
                  (selectedAgentList as unknown as number[] | string[]) ||
                  (selectedAgent as number[] | string[]);
                fetchReminders(
                  apiStart,
                  apiEnd,
                  currentSearch,
                  currentFilters.reminderType,
                  currentFilters.status,
                  effectiveUserId,
                  sortBy,
                  undefined,
                  next
                );
              }}
              onSearchChange={(searchTerm: string) => {
                console.log(
                  "Main page - List view search changed to:",
                  searchTerm
                );
                setCurrentSearch(searchTerm);
                let apiStart: Date | undefined;
                let apiEnd: Date | undefined;
                if (filterDate && filterDate.startDate && filterDate.endDate) {
                  apiStart = moment(filterDate.startDate).toDate();
                  apiEnd = moment(filterDate.endDate).toDate();
                } else {
                  [apiStart, apiEnd] = toApiRange(
                    currentViewStart,
                    currentViewEnd
                  );
                }
                fetchReminders(
                  apiStart,
                  apiEnd,
                  searchTerm,
                  currentFilters.reminderType,
                  currentFilters.status,
                  selectedAgent as number[] | string[],
                  sortBy,
                  undefined,
                  sortOrder
                );
              }}
              onFilterChange={(filters: {
                reminderType?: string;
                status?: string;
                userId?: string | number | (string | number)[] | undefined;
              }) => {
                console.log(
                  "Main page - List view filters changed to:",
                  filters
                );
                const hasUserId = Object.prototype.hasOwnProperty.call(
                  filters,
                  "userId"
                );
                setCurrentFilters((prev) => ({
                  reminderType:
                    filters.reminderType !== undefined
                      ? filters.reminderType
                      : prev.reminderType,
                  status:
                    filters.status !== undefined ? filters.status : prev.status,
                  userId: hasUserId
                    ? (filters.userId as any)
                    : prev.userId || (selectedAgent as any),
                }));
                // Always refresh the list when filters change. Use the
                // currently selected calendar range if available, otherwise
                // let fetchReminders fall back to its default range.
                // if (Object.prototype.hasOwnProperty.call(filters, "userId")) {
                //   setSelectedAgentId(filters.userId ?? undefined);
                // }
                const nextSelected = Object.prototype.hasOwnProperty.call(
                  filters,
                  "userId"
                )
                  ? (filters.userId as any) ?? undefined
                  : selectedAgentList;
                const [apiStart, apiEnd] = toApiRange(
                  currentViewStart,
                  currentViewEnd
                );

                const effectiveUserId = hasUserId
                  ? (filters.userId as any)
                  : currentFilters.userId || (selectedAgent as any);
                fetchReminders(
                  apiStart,
                  apiEnd,
                  currentSearch,
                  filters.reminderType !== undefined
                    ? filters.reminderType
                    : currentFilters.reminderType,
                  filters.status !== undefined
                    ? filters.status
                    : currentFilters.status,
                  effectiveUserId,
                  sortBy,
                  undefined,
                  sortOrder
                );
              }}
              onRetry={() => {
                let apiStart: Date | undefined;
                let apiEnd: Date | undefined;
                if (filterDate && filterDate.startDate && filterDate.endDate) {
                  apiStart = moment(filterDate.startDate).toDate();
                  apiEnd = moment(filterDate.endDate).toDate();
                } else {
                  [apiStart, apiEnd] = toApiRange(
                    currentViewStart,
                    currentViewEnd
                  );
                }
                fetchReminders(
                  apiStart,
                  apiEnd,
                  currentSearch,
                  currentFilters.reminderType,
                  currentFilters.status,
                  selectedAgent as number[] | string[],
                  sortBy,
                  undefined,
                  sortOrder
                );
              }}
              onEditReminder={handleEditReminder}
              onDeleteReminder={handleDeleteReminder}
              onDateRangeChange={(dateRange: DateValueType) => {
                console.log(
                  "Main page - List view date range changed to:",
                  dateRange
                );
                if (dateRange && dateRange.startDate && dateRange.endDate) {
                  const startDate = moment(dateRange.startDate).toDate();
                  const endDate = moment(dateRange.endDate).toDate();
                  // Keep filterDate in sync for list view sorting
                  setFilterDate(dateRange);
                  setCurrentViewStart(startDate);
                  setCurrentViewEnd(endDate);
                  fetchReminders(
                    startDate,
                    endDate,
                    currentSearch,
                    currentFilters.reminderType,
                    currentFilters.status,
                    selectedAgent as number[] | string[],
                    sortBy,
                    undefined,
                    sortOrder
                  );
                }
              }}
              search={search}
              setSearch={setSearch}
            />
          )}
        </div>
      </div>
      <CreateReminderPanel
        visible={showAddModal}
        setVisible={(visible) => {
          setShowAddModal(visible);
          if (!visible) {
            // Reset edit mode when closing modal
            setEditMode(false);
            setEditReminderId("");
            setEditReminderData(null);
          }
        }}
        onSuccess={(created) => {
          // Let the existing triggerRefresh logic handle reloading the UI.
          // We still log the created item for debugging.
          console.log("CreateReminderPanel.onSuccess, created:", created);
          triggerRefresh();
        }}
        editMode={editMode}
        editReminderId={editReminderId}
        editReminderData={editReminderData}
      />
      {/* Event Detail Modal */}
      <Transition appear show={showEventDetail} as={Fragment}>
        <HeadlessDialog
          as="div"
          className="relative z-50"
          onClose={() => setShowEventDetail(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <HeadlessDialog.Panel
                  className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
                  onClick={(e) => e.stopPropagation()}
                >
                  {selectedEvent && (
                    <>
                      {/* Header with close button */}
                      <div className="flex justify-between items-center mb-6">
                        <HeadlessDialog.Title className="text-lg font-semibold text-slate-800">
                          Reminder Details
                        </HeadlessDialog.Title>
                        <button
                          onClick={() => setShowEventDetail(false)}
                          className="p-1 hover:bg-gray-100 rounded-full"
                        >
                          <Lucide icon="X" className="w-5 h-5 text-gray-500" />
                        </button>
                      </div>

                      {/* Title */}
                      <div className="mb-4">
                        <h3 className="text-base font-medium text-slate-900 mb-2">
                          {selectedEvent.title}
                        </h3>
                        <p className="text-sm text-slate-600">
                          {selectedEvent.description}
                        </p>
                      </div>

                      {/* Details Grid */}
                      <div className="space-y-4 mb-6">
                        {/* Status Badge */}
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-slate-700">
                            Status
                          </span>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              selectedEvent.status === "completed"
                                ? "bg-green-100 text-green-700"
                                : selectedEvent.status === "overdue"
                                ? "bg-red-100 text-red-700"
                                : selectedEvent.status === "snoozed"
                                ? "bg-orange-100 text-orange-700"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {selectedEvent.status?.toUpperCase() || "PENDING"}
                          </span>
                        </div>

                        {/* Reminder Type */}
                        {selectedEvent.reminderType && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-slate-700">
                              Type
                            </span>
                            <span className="text-sm text-slate-900 capitalize">
                              {selectedEvent.reminderType.replace("_", " ")}
                            </span>
                          </div>
                        )}

                        {/* Created Time */}
                        {selectedEvent.assignments &&
                          selectedEvent.assignments.length > 0 &&
                          selectedEvent.assignments[0].created_at && (
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-slate-700">
                                Created
                              </span>
                              <div className="flex items-center text-sm text-slate-900">
                                <Lucide
                                  icon="Calendar"
                                  className="w-4 h-4 mr-1 text-slate-500"
                                />
                                {moment(
                                  selectedEvent.assignments[0].created_at
                                ).format("MMM DD, YYYY")}
                                <span className="mx-1 text-slate-400">•</span>
                                <Lucide
                                  icon="Clock"
                                  className="w-4 h-4 mr-1 text-slate-500"
                                />
                                {moment(
                                  selectedEvent.assignments[0].created_at
                                ).format("HH:mm")}
                              </div>
                            </div>
                          )}

                        {/* Assigned To */}
                        {selectedEvent.assignedTo && (
                          <div className="flex justify-between items-start">
                            <span className="text-sm font-medium text-slate-700">
                              Assigned To
                            </span>
                            <div className="text-sm text-slate-900 text-right max-w-[200px]">
                              <div className="flex items-center justify-end">
                                <Lucide
                                  icon="Users"
                                  className="w-4 h-4 mr-1 text-slate-500"
                                />
                                <span>{selectedEvent.assignedTo}</span>
                              </div>
                              {/* Show individual assignment status if available */}
                              {selectedEvent.assignedUsers &&
                                selectedEvent.assignedUsers.length > 1 && (
                                  <div className="mt-1 text-xs text-slate-500">
                                    {selectedEvent.assignedUsers.length} users
                                    assigned
                                  </div>
                                )}
                            </div>
                          </div>
                        )}

                        {/* Reminder Before */}
                        {selectedEvent.reminderBeforeMinutes !== undefined && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-slate-700">
                              Reminder Before
                            </span>
                            <span className="text-sm text-slate-900">
                              {selectedEvent.reminderBeforeMinutes === 0
                                ? "At time"
                                : `${selectedEvent.reminderBeforeMinutes} min${
                                    selectedEvent.reminderBeforeMinutes > 1
                                      ? "s"
                                      : ""
                                  } before`}
                            </span>
                          </div>
                        )}

                        {displayedQueryNo && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-slate-700">
                              Query ID
                            </span>
                            <span className="text-sm text-slate-900">
                              {`#${displayedQueryNo}`}
                            </span>
                          </div>
                        )}

                        {selectedEvent.createdFor && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-slate-700">
                              Created By
                            </span>
                            <div className="flex items-center text-sm text-slate-900">
                              <Lucide
                                icon="User"
                                className="w-4 h-4 mr-1 text-slate-500"
                              />
                              {selectedEvent.createdFor}
                            </div>
                          </div>
                        )}

                        {selectedEvent.scheduledTime && (
                          <div>
                            <div className="text-sm font-medium text-slate-700 mb-2">
                              Scheduled Time
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded text-sm">
                                <Lucide
                                  icon="Calendar"
                                  className="w-4 h-4 mr-1"
                                />
                                {selectedEvent.scheduledTime.split(" 🕐 ")[0]}
                              </div>
                              <div className="flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded text-sm">
                                <Lucide icon="Clock" className="w-4 h-4 mr-1" />
                                {selectedEvent.scheduledTime.split(" 🕐 ")[1]}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons - Redesigned */}
                      {selectedEvent.status !== "completed" &&
                      selectedEvent.status !== "snoozed" ? (
                        // Active reminders - normal layout
                        <div className="flex items-center justify-between border-t border-slate-200 pt-4">
                          {/* Left side - Icon buttons with tooltips */}
                          <div className="flex items-center space-x-2">
                            {/* View More Detail Icon */}
                            {checkPermission(reminderAPIUrls.detailReminder) &&
                            !isSuperAdmin() ? (
                              <Tippy content="View More Detail">
                                <button
                                  onClick={() => {
                                    setShowEventDetail(false);
                                    handleViewDetailPage(selectedEvent);
                                  }}
                                  className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="View More Detail"
                                >
                                  <Lucide
                                    icon="ExternalLink"
                                    className="w-5 h-5"
                                  />
                                </button>
                              </Tippy>
                            ) : null}

                            {/* Edit Icon */}
                            {checkPermission(reminderAPIUrls.updateReminder) &&
                            !isSuperAdmin() ? (
                              <Tippy content="Edit Reminder">
                                <button
                                  onClick={() => {
                                    setShowEventDetail(false);
                                    if (selectedEvent.query) {
                                      handleEditReminder(selectedEvent.query);
                                    }
                                  }}
                                  className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="Edit"
                                >
                                  <Lucide icon="Pencil" className="w-5 h-5" />
                                </button>
                              </Tippy>
                            ) : null}

                            {/* Delete Icon */}
                            {checkPermission(reminderAPIUrls.deleteReminder) &&
                            !isSuperAdmin() ? (
                              <Tippy content="Delete Reminder">
                                <button
                                  onClick={() => {
                                    setShowEventDetail(false);
                                    if (selectedEvent.query) {
                                      handleDeleteReminder(selectedEvent.query);
                                    }
                                  }}
                                  className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete"
                                >
                                  <Lucide icon="Trash2" className="w-5 h-5" />
                                </button>
                              </Tippy>
                            ) : null}
                          </div>

                          {/* Right side - Action buttons */}
                          <div className="flex items-center space-x-2">
                            {selectedEvent.status !== "overdue" && (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => {
                                  setShowEventDetail(false);
                                  setTimeout(
                                    () => setShowSnoozeModal(true),
                                    100
                                  );
                                }}
                              >
                                <Lucide icon="Clock" className="w-4 h-4 mr-1" />
                                Snooze
                              </Button>
                            )}
                            {checkPermission(
                              reminderAPIUrls.completeReminder
                            ) && !isSuperAdmin() ? (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => {
                                  setShowEventDetail(false);
                                  setTimeout(
                                    () => setShowCompleteModal(true),
                                    100
                                  );
                                }}
                              >
                                <Lucide icon="Check" className="w-4 h-4 mr-1" />
                                Complete
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      ) : (
                        // Completed/Snoozed reminders - layout with icon buttons and view detail
                        <div className="flex items-center justify-between border-t border-slate-200 pt-4 mb-3">
                          {/* Left side - Icon buttons with tooltips */}
                          <div className="flex items-center space-x-2">
                            {/* View More Detail Icon */}
                            <Tippy content="View More Detail">
                              <button
                                onClick={() => {
                                  setShowEventDetail(false);
                                  handleViewDetailPage(selectedEvent);
                                }}
                                className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              >
                                <Lucide
                                  icon="ExternalLink"
                                  className="w-5 h-5"
                                />
                              </button>
                            </Tippy>
                            {selectedEvent.status !== "completed" && (
                              <>
                                <Tippy content="Edit Reminder">
                                  <button
                                    onClick={() => {
                                      setShowEventDetail(false);
                                      if (selectedEvent.query) {
                                        handleEditReminder(selectedEvent.query);
                                      }
                                    }}
                                    className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  >
                                    <Lucide icon="Pencil" className="w-5 h-5" />
                                  </button>
                                </Tippy>

                                <Tippy content="Delete Reminder">
                                  <button
                                    onClick={() => {
                                      setShowEventDetail(false);
                                      if (selectedEvent.query) {
                                        handleDeleteReminder(
                                          selectedEvent.query
                                        );
                                      }
                                    }}
                                    className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  >
                                    <Lucide icon="Trash2" className="w-5 h-5" />
                                  </button>
                                </Tippy>
                              </>
                            )}
                          </div>

                          {/* Right side - View More Detail Button */}
                          {/* <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              setShowEventDetail(false);
                              handleViewDetailPage(selectedEvent);
                            }}
                          >
                            <Lucide
                              icon="ExternalLink"
                              className="w-4 h-4 mr-2"
                            />
                            View More Detail
                          </Button> */}
                        </div>
                      )}

                      {/* Show completion status for completed/snoozed reminders */}
                      {(selectedEvent.status === "completed" ||
                        selectedEvent.status === "snoozed") && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center">
                            <Lucide
                              icon={
                                selectedEvent.status === "completed"
                                  ? "CheckCircle"
                                  : "Clock"
                              }
                              className={`w-5 h-5 mr-2 ${
                                selectedEvent.status === "completed"
                                  ? "text-green-600"
                                  : "text-blue-600"
                              }`}
                            />
                            <span
                              className={`font-medium ${
                                selectedEvent.status === "completed"
                                  ? "text-green-900"
                                  : "text-blue-900"
                              }`}
                            >
                              {selectedEvent.status === "completed"
                                ? "This reminder has been completed"
                                : "This reminder has been snoozed"}
                            </span>
                          </div>

                          {/* Show completion details if available */}
                          {selectedEvent.assignments &&
                            selectedEvent.assignments.length > 0 && (
                              <div className="mt-3 text-sm text-slate-600">
                                {selectedEvent.assignments.map(
                                  (assignment: any, index: number) => (
                                    <div
                                      key={assignment.id || index}
                                      className="mb-2"
                                    >
                                      {assignment.completed_at && (
                                        <div>
                                          <span className="font-medium">
                                            Completed:
                                          </span>{" "}
                                          {moment(
                                            assignment.completed_at
                                          ).format("MMM DD, YYYY HH:mm")}
                                        </div>
                                      )}
                                      {assignment.completion_remarks && (
                                        <div>
                                          <span className="font-medium">
                                            Remarks:
                                          </span>{" "}
                                          {assignment.completion_remarks}
                                        </div>
                                      )}
                                      {assignment.snoozed_until && (
                                        <div>
                                          <span className="font-medium">
                                            Snoozed Until:
                                          </span>{" "}
                                          {moment(
                                            assignment.snoozed_until
                                          ).format("MMM DD, YYYY HH:mm")}
                                        </div>
                                      )}
                                    </div>
                                  )
                                )}
                              </div>
                            )}
                        </div>
                      )}
                    </>
                  )}
                </HeadlessDialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </HeadlessDialog>
      </Transition>
      {/* Snooze Modal */}
      <Transition appear show={showSnoozeModal} as={Fragment}>
        <HeadlessDialog
          as="div"
          className="relative z-50"
          onClose={() => setShowSnoozeModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <HeadlessDialog.Panel
                  className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header with close button */}
                  <div className="flex justify-between items-center mb-4">
                    <HeadlessDialog.Title className="text-lg font-semibold text-slate-800">
                      Snooze Reminder
                    </HeadlessDialog.Title>
                    <button
                      onClick={() => setShowSnoozeModal(false)}
                      className="p-1 hover:bg-gray-100 rounded-full"
                    >
                      <Lucide icon="X" className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  <p className="text-sm text-slate-600 mb-6">
                    Choose how long to snooze this reminder
                  </p>

                  {/* Snooze Duration */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Snooze Duration
                    </label>
                    <FormSelect
                      value={snoozeTime}
                      onChange={(e) => setSnoozeTime(e.target.value)}
                      className="w-full"
                    >
                      <option value="15 Minutes">15 Minutes</option>
                      <option value="30 Minutes">30 Minutes</option>
                      <option value="1 Hour">1 Hour</option>
                      <option value="2 Hours">2 Hours</option>
                      <option value="4 Hours">4 Hours</option>
                      <option value="1 Day">1 Day</option>
                      <option value="2 Days">2 Days</option>
                      <option value="1 Week">1 Week</option>
                    </FormSelect>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    <Button
                      variant="outline-secondary"
                      className="flex-1"
                      onClick={() => setShowSnoozeModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      className="flex-1"
                      onClick={handleSnoozeReminder}
                    >
                      Snooze
                    </Button>
                  </div>
                </HeadlessDialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </HeadlessDialog>
      </Transition>
      {/* Complete Modal */}
      <Transition appear show={showCompleteModal} as={Fragment}>
        <HeadlessDialog
          as="div"
          className="relative z-50"
          onClose={() => setShowCompleteModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <HeadlessDialog.Panel
                  className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header with close button */}
                  <div className="flex justify-between items-center mb-4">
                    <HeadlessDialog.Title className="text-lg font-semibold text-slate-800">
                      Mark as Complete
                    </HeadlessDialog.Title>
                    <button
                      onClick={() => setShowCompleteModal(false)}
                      className="p-1 hover:bg-gray-100 rounded-full"
                    >
                      <Lucide icon="X" className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  <p className="text-sm text-slate-600 mb-6">
                    Add any remarks or notes about the completion of this
                    reminder (optional).
                  </p>

                  {/* Completion Remarks */}
                  <div className="mb-6">
                    <FormInput
                      type="text"
                      placeholder="Enter completion remarks..."
                      value={completionRemarks}
                      onChange={(e) => setCompletionRemarks(e.target.value)}
                      className="w-full"
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    <Button
                      variant="outline-secondary"
                      className="flex-1"
                      onClick={() => {
                        setCompletionRemarks("");
                        setShowCompleteModal(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      className="flex-1"
                      onClick={handleCompleteReminder}
                    >
                      Mark Complete
                    </Button>
                  </div>
                </HeadlessDialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </HeadlessDialog>
      </Transition>
      {/* Delete Confirmation Modal */}
      <Transition appear show={showDeleteModal} as={Fragment}>
        <HeadlessDialog
          as="div"
          className="relative z-50"
          onClose={() => setShowDeleteModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <HeadlessDialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                    <Lucide
                      icon="AlertTriangle"
                      className="w-6 h-6 text-red-600"
                    />
                  </div>

                  <HeadlessDialog.Title className="text-lg font-semibold text-slate-800 text-center mb-2">
                    Delete Reminder
                  </HeadlessDialog.Title>

                  <p className="text-sm text-slate-600 text-center mb-6">
                    Are you sure you want to delete this reminder? This action
                    cannot be undone.
                  </p>

                  <div className="flex gap-3 justify-center">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={cancelDeleteReminder}
                      className="px-6"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={confirmDeleteReminder}
                      className="px-6"
                    >
                      <Lucide icon="Trash2" className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </HeadlessDialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </HeadlessDialog>
      </Transition>
      {/* Reminder Export Slideover */}
      <ReminderExport
        visible={showExportModal}
        setVisible={(v: boolean) => setShowExportModal(v)}
        dataList={(() => {
          const map: Record<string, any> = {};
          apiReminders.forEach((r) => {
            (r.assignedUsers || []).forEach((u: any) => {
              if (u && u.id) map[u.id] = u;
            });
          });
          return Object.values(map).map((u: any) => ({
            label: `${u.name} ${u.last_name || ""}`.trim(),
            val: u.id,
          }));
        })()}
      />
    </div>
  );
}

const mapStateToProps = ({}: RootState) => ({});

const mapState = ({ users }: RootState) => ({ users: users?.data || [] });

const mapDispatchToProps = (dispatch: any) => ({
  fetchUsers: (payload: any) => dispatch(fetchAllUsers(payload)),
});
export default connect(mapState, mapDispatchToProps)(Component);
